import { uvToSphereGlsl, applyInverseWarpGlsl } from './glslUtils';

/** Stamp shader — composites an arbitrary texture (canvas stamp or image) onto
 *  the sphere render-target layer.
 *
 *  Three projection modes (uProjMode uniform):
 *  - 0 (wrap):    tangent-plane projection from the stamp center outward;
 *                 works anywhere on the sphere, used for layer-local stamps.
 *  - 1 (project): camera NDC bounds; clips back-facing fragments. Used when
 *                 placing or duplicating a camera-projected stamp.
 *  - 2 (inverse): same NDC framing as project, but UVs are derived from where
 *                 camera rays exit the back hemisphere. Produces a concave
 *                 look from the current camera perspective.
 *
 *  Rotation (uStampRotation radians) is applied in physical (square) space
 *  with aspect correction so 1:1 aspect images rotate without distortion.
 *
 *  uRotFwd allows stamp copies to be baked at rotated positions (duplication).
 *  For a non-rotated stamp pass THREE.Matrix3 identity.
 *
 *  Output is straight-alpha Porter-Duff over, matching all layer RTs.
 */

export const stampVertexShader = /*glsl*/`
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position, 1.0); }
`;

export const stampFragmentShader = /*glsl*/`
  precision highp float;
  #define PI 3.14159265359
  varying vec2 vUv;
  uniform sampler2D uBase;
  uniform sampler2D uStamp;
  uniform vec3 uStampCenter;
  uniform vec3 uStampRight;
  uniform vec3 uStampUp;
  uniform vec2 uStampScale;
  uniform float uStampOpacity;
  uniform vec3 uStampColor;
  uniform mat3 uRotFwd;
  uniform int  uProjMode;
  uniform mat4 uCamViewProj;
  uniform vec2 uStampCenterNDC;
  uniform vec2 uSphereCenterNDC;
  uniform vec2 uStampHalfSizeNDC;
  uniform float uInverseWarpK;
  uniform float uInverseWarpStrength;
  uniform vec3 uCamPos;
  uniform float uStampRotation;
  uniform float uStampAspect;
  uniform int   uStampFlipX;
  uniform int   uStampFlipY;
  uniform vec2  uStampCenterUV;

${uvToSphereGlsl}${applyInverseWarpGlsl}
  void main() {
    vec4 base = texture(uBase, vUv);
    // Layer-local direction → world direction
    vec3 localDir = uvToSphere(vUv);
    vec3 worldDir = uRotFwd * localDir;

    vec2 sUV;
    if (uProjMode == 1) {
      // Projection mode: clip any surface not facing the (possibly rotated)
      // camera.  uCamPos is updated per-copy on the CPU side.
      if (dot(worldDir, uCamPos) <= 0.0) { gl_FragColor = base; return; }
      vec4 clipPos = uCamViewProj * vec4(worldDir, 1.0);
      if (clipPos.w <= 0.0) { gl_FragColor = base; return; }
      vec2 ndc = clipPos.xy / clipPos.w;
      sUV = (ndc - uStampCenterNDC) / (uStampHalfSizeNDC * 2.0) + 0.5;
    } else if (uProjMode == 2) {
      // Inverse mode:
      // 1) keep only front-facing fragments;
      // 2) trace camera ray through front point to back-sphere exit;
      // 3) project that back hit to NDC;
      // 4) apply a ROUND radial warp around the stamp center to avoid pinching,
      //    but drive inward/outward direction by the stamp center's distance
      //    from projected sphere center so the behavior is sphere-locked.
      if (dot(worldDir, uCamPos) <= 0.0) { gl_FragColor = base; return; }

      float camDist2 = dot(uCamPos, uCamPos);
      vec3 rayFrag = worldDir - uCamPos;
      float tBack = (camDist2 - 1.0) / dot(rayFrag, rayFrag);
      if (tBack <= 0.0) { gl_FragColor = base; return; }
      vec3 pBack = uCamPos + tBack * rayFrag;

      vec4 clipPos = uCamViewProj * vec4(pBack, 1.0);
      if (clipPos.w <= 0.0) { gl_FragColor = base; return; }
      vec2 ndc = clipPos.xy / clipPos.w;
      vec2 ndcWarped = applyInverseWarp(ndc);

      vec2 local = (ndcWarped - uStampCenterNDC) / (uStampHalfSizeNDC * 2.0);
      sUV = local + 0.5;
    } else {
      // Wrap mode: azimuthal equidistant projection centred on uStampCenter.
      // Uses the tangent frame (uStampRight, uStampUp) so the stamp looks
      // identical at any latitude — no latlong pinching at the poles.
      // uStampScale = (diameter_radians * aspect, diameter_radians).
      vec3 c = normalize(uStampCenter);
      float cosTheta = clamp(dot(worldDir, c), -1.0, 1.0);
      float theta    = acos(cosTheta);
      vec3 perp      = worldDir - cosTheta * c;
      float perpLen  = length(perp);
      vec2 local2d;
      if (perpLen > 0.0001) {
        vec3 perpDir = perp / perpLen;
        local2d = vec2(dot(perpDir, uStampRight), dot(perpDir, uStampUp)) * theta;
      } else {
        local2d = vec2(0.0);
      }
      sUV = local2d / uStampScale + 0.5;
    }

    // Rotate sUV around stamp center, correcting for image aspect ratio
    // so that rotation is always in physical (square) space.
    if (uStampRotation != 0.0) {
      float cosR = cos(uStampRotation);
      float sinR = sin(uStampRotation);
      vec2 sc = sUV - 0.5;
      sc.x *= uStampAspect;  // stretch to square physical space
      sc = vec2(cosR * sc.x - sinR * sc.y, sinR * sc.x + cosR * sc.y);
      sc.x /= uStampAspect;  // restore aspect
      sUV = sc + 0.5;
    }

    if (uStampFlipX != 0) {
      sUV.x = 1.0 - sUV.x;
    }
    if (uStampFlipY != 0) {
      sUV.y = 1.0 - sUV.y;
    }

    if (any(lessThan(sUV, vec2(0.0))) || any(greaterThan(sUV, vec2(1.0)))) {
      gl_FragColor = base; return;
    }

    vec4 stamp = texture(uStamp, sUV);
    // Tint the stamp by the brush color (icon presets are white, so white×color = color).
    stamp.rgb *= uStampColor;
    // Both stamp and base use straight alpha (layer RTs store straight alpha;
    // canvas textures are unpremultiplied by the browser before upload).
    // Porter-Duff over for straight-alpha inputs, straight-alpha output.
    float sA = stamp.a * uStampOpacity;
    float outA = sA + base.a * (1.0 - sA);
    vec3 outRGB = outA > 0.0
      ? (stamp.rgb * sA + base.rgb * base.a * (1.0 - sA)) / outA
      : vec3(0.0);
    gl_FragColor = vec4(outRGB, outA);
  }
`;
